
// react-bootstrap imports
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { Button, Form } from "react-bootstrap"

// components imports
import { BotaoVoltar, LoadingSpinner } from "components/Helpers"
import { BotaoAutoPreenchimento } from "components/BotoesAuxiliaresDev"
import { AlertMessage } from "components/AlertMessages"
import { AvisoGuiaExistente, BotaoLoading, autoFillWithCache, saveInputInCache } from "components/FormGerarGuia"

// service imports
import * as service from "services/TaxasCTM24/CalcularTaxaServico/GerarGuiaDePagamento"

// assets imports
import stdStyle from "json/StdStyles.json"
import { useAlert } from "hooks/useAlert"
import { section } from "services/InputFieldsGuia"
import { TIPO } from "constants/tiposDoc"

const linkNovaCalcularTaxa = "/NovaCalculadoraDeTaxas/NovaCalcularTaxa/"


const BotaoAtualizarGuia = ({ isLoading, ehMulta }) =>
{
    if (isLoading) { return <BotaoLoading />}

    const nomeBotao = ehMulta ? "Atualizar multa" : "Atualizar guia de pagamento"
    return <Button className={stdStyle.button} variant="success" type="submit">{nomeBotao}</Button>
}

const BotaoBaixarGuia = ({ isLoading, ehMulta }) =>
{
    if (isLoading) { return <BotaoLoading />}

    const nomeBotao = ehMulta ? "Baixar multa" : "Baixar guia de pagamento"
    return <Button className={stdStyle.button} variant="success" type="submit">{nomeBotao}</Button>
}

const BotaoSalvarDados = ({ setShowAlertDataSaved }) =>
{
    return <Button className="rounded-5" variant="outline-primary" onClick={() => {saveInputInCache(setShowAlertDataSaved)}}>Salvar dados</Button>
}

const BotaoCarregarDados = ({ setShowAlertNoData }) =>
{
    return <Button className="rounded-5" variant="outline-primary" onClick={() => {autoFillWithCache(setShowAlertNoData)}}>Carregar dados</Button>
}    

const Botoes = ({ modoEditar, isLoading, ehMulta, linkPrevPage, statePrevPage, setAlerts }) =>
{
    const divStyle = "d-flex justify-content-around mb-2 mt-2"
    return (
        <>
            <hr className="text-primary opacity-100" />
            <div className={divStyle} >
                <BotaoVoltar linkPrevPage={linkPrevPage} statePrevPage={statePrevPage} />
                {modoEditar ? <BotaoAtualizarGuia isLoading={isLoading} ehMulta={ehMulta} /> : <BotaoBaixarGuia isLoading={isLoading} ehMulta={ehMulta} /> }
            </div>
            <div className={divStyle}>
                <BotaoSalvarDados setShowAlertDataSaved={(show) => {setAlerts(show ? 3 : 0)}}  />
                <BotaoCarregarDados setShowAlertNoData={(show) => {setAlerts(show ? 1 : 0)}} />
            </div>
        </>
    )
}

const BotaoPreencherComDadosProtocolo = ({ dadosProtocolo }) =>
{
    if (!dadosProtocolo) { return }

    return <Button className="rounded-5" variant="outline-primary" onClick={() => {service.preencherComDadosProtocolo(dadosProtocolo)}}>Preencher com dados do protocolo</Button>
}

const SecoesGuiaPag = ({ onBlurProcesso, switchCNPJ, setSwitchCNPJ, servicoTemDadosObra, servicoTemParcelamento }) =>
{
    return (
        <>
            { section.dadosProcesso(onBlurProcesso) }
            { section.dadosProprietario(switchCNPJ, () => {service.onChangeCPFCNPJ(setSwitchCNPJ)}) }
            { section.enderecoRequerente(servicoTemDadosObra) }
            { servicoTemDadosObra ? section.enderecoObra(service.AutoCompleteAddressObra) : <></> }
            { servicoTemParcelamento ? section.parcelamento() : <></> }
        </>
    )
}

export const GerarGuiaDePagamentoScreen = () =>
{
    const {alert, showAlert, closeAlert} = useAlert()
    const location = useLocation()
    const [alerts, setAlerts] = useState(0)
    const [isLoading, setLoading] = useState(false)
    const [validated, setValidated] = useState(false)
    const [switchCNPJ, setSwitchCNPJ] = useState(false)
    const [dadosProtocolo, setDadosProtocolo] = useState()
    const [informedProcData, setInformedProcData] = useState(false)
    const [showAvisoGuiaExistente, setShowAvisoGuiaExistente] = useState(false)

    const { servicoTaxa, procData, guiaData, taxInputs, taxResults, editar, linkPrevPage, statePrevPage } = location.state
    
    useEffect(() => {
        if (editar)
        {
            service.autoFillGuia(procData, guiaData)
        }
    }, [editar, procData, guiaData])
    
    const servicoTemParcelamento = service.temParcelamento(guiaData?.fatoGerador, servicoTaxa.id)
    const servicoTemDadosObra = !!guiaData?.dadosObra || servicoTaxa.id === TIPO.AutObrasInfra    

    const onFormSubmit = async event => {
        service.handleSubmit(event, setLoading, setValidated, guiaData, servicoTaxa, taxInputs, taxResults, editar, procData?.id, showAlert)
    }

    async function handleOnBlurProcesso()
    {
        setDadosProtocolo(await service.checkTemGuiaProtocolo(editar, servicoTaxa.nome, taxInputs.tipoImovel))
        service.handleAvisoGuiaExistente(guiaData?.fatoGerador, servicoTaxa, taxInputs, editar, setInformedProcData, setShowAvisoGuiaExistente)
    }

    return (
        <>
            <AlertMessage show={alert.show} variant={alert.variant} header={alert.header} message={alert.message} onClose={closeAlert} />

            <Form className={stdStyle.formGuia} noValidate validated={validated} onSubmit={onFormSubmit}>

                <AvisoGuiaExistente procData={informedProcData} showAvisoGuiaExistente={showAvisoGuiaExistente} />
                <p className="text-muted fs-7">* Campos com asterisco são de preenchimento obrigatório</p>
                <BotaoPreencherComDadosProtocolo dadosProtocolo={dadosProtocolo} />
                
                <SecoesGuiaPag onBlurProcesso={handleOnBlurProcesso} switchCNPJ={switchCNPJ} setSwitchCNPJ={setSwitchCNPJ} servicoTemDadosObra={servicoTemDadosObra} servicoTemParcelamento={servicoTemParcelamento} />
                
                <Botoes modoEditar={editar} isLoading={isLoading} ehMulta={false} alerts={alerts} setAlerts={setAlerts} linkPrevPage={linkPrevPage} statePrevPage={statePrevPage}/>
                <BotaoAutoPreenchimento />

            </Form>
            
            <LoadingSpinner show={isLoading} />
        </>
    )
}